import { Check, ChevronDown, ChevronsUpDown, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SimpleTooltip, SimpleTooltipV2 } from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/UserAvatar';
import { cn } from '@/utils';
import { formatAssigneeTooltip, resolveAssigneeTeamName } from '@/utils/assigneeTeam';

/**
 * A single selectable assignee.
 */
export interface AssigneeOption {
  /** Stable unique identifier (typically user_id). */
  id: string;
  /** Display name (falls back to email when missing). */
  name?: string;
  /** Email — used as the value passed to onChange. */
  email: string;
  /** Optional online indicator. */
  available?: boolean;
}

/**
 * A group of assignees (e.g., team) shown as a labelled section.
 */
export interface AssigneeOptionGroup {
  /** Stable unique identifier for the group (typically teamId). */
  id: string;
  /** Group heading shown above its options. */
  label: string;
  /** Members in the group. */
  options: AssigneeOption[];
}

type AssigneeOptions = AssigneeOption[] | AssigneeOptionGroup[];

type AssigneeDropdownVariant = 'default' | 'inline' | 'ghost';

interface AssigneeDropdownBaseProps {
  /** Flat list of options or grouped options. */
  options: AssigneeOptions;
  /** Visual style of the trigger. */
  variant?: AssigneeDropdownVariant;
  /** Trigger placeholder when nothing is selected. */
  placeholder?: string;
  /** Optional override for the trigger label (replaces selection display). */
  triggerLabel?: string;
  /** Optional icon to show in the trigger. */
  triggerIcon?: React.ComponentType<{ className?: string }>;
  /** Show a search input above the list. Defaults to true. */
  searchable?: boolean;
  /** Search input placeholder. */
  searchPlaceholder?: string;
  /** Disable the trigger. */
  disabled?: boolean;
  /** Tooltip shown when disabled (only when wrapped). */
  disabledTooltip?: string;
  /** Custom row renderer. Defaults to avatar + name + email. */
  renderOption?: (option: AssigneeOption) => React.ReactNode;
  /** Empty state when search has no matches. */
  emptyText?: string;
  /** Called when the popover opens (e.g., to refetch). */
  onOpen?: () => void;
  /** Additional className for the trigger. */
  className?: string;
  /** Test id for the trigger. */
  triggerTestId?: string;
}

interface SingleSelectProps extends AssigneeDropdownBaseProps {
  mode: 'single';
  selected: string;
  /**
   * Currently selected team id. When set, only the matching email *under this team's group*
   * is marked selected — so a user who appears in several teams is ticked once, under the
   * right team. Ignored for flat (ungrouped) options.
   */
  selectedTeamId?: string;
  /** Called with the selected email and, when chosen from a team group, that team's id. */
  onChange: (value: string, teamId?: string) => void;
}

interface MultiSelectProps extends AssigneeDropdownBaseProps {
  mode: 'multi';
  selected: string[];
  onChange: (value: string[]) => void;
  /** Show "N selected" instead of names when count exceeds this. Defaults to 4. */
  maxCount?: number;
}

export type AssigneeDropdownProps = SingleSelectProps | MultiSelectProps;

const TRIGGER_VARIANT_CLASSES: Record<AssigneeDropdownVariant, string> = {
  default: 'w-full justify-between',
  inline:
    'h-8 border-none bg-transparent outline-hidden focus:ring-0 focus-visible:ring-0 ring-0 text-sm px-1 w-auto max-w-full min-w-0 gap-1 hover:bg-accent/30 justify-between',
  ghost:
    'h-auto p-0 bg-transparent hover:bg-transparent text-background hover:text-background/70 text-sm font-medium focus:ring-0 focus:ring-offset-0 focus:outline-hidden flex items-center w-auto whitespace-nowrap shrink-0 transition-opacity disabled:opacity-50',
};

const isGroupedOptions = (options: AssigneeOptions): options is AssigneeOptionGroup[] =>
  options.length > 0 && 'options' in options[0];

const sortByLabel = (options: AssigneeOption[]): AssigneeOption[] =>
  [...options].sort((a, b) => {
    const aLabel = a.name || a.email;
    const bLabel = b.name || b.email;
    return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base' });
  });

const defaultRenderOption = (option: AssigneeOption): React.ReactNode => {
  // Prefer the display name, fall back to email when no name is available.
  const label = option.name || option.email || '';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <UserAvatar
        name={option.name}
        email={option.email}
        userId={option.id}
        size="sm"
        available={option.available ?? false}
      />
      <span className="truncate text-sm">{label}</span>
    </div>
  );
};

/**
 * Unified assignee dropdown — used by the cases list filter, per-case assignee cell,
 * and the bulk-assign action bar. Supports single/multi selection, flat or grouped
 * options, alphabetical sorting, and search.
 */
export function AssigneeDropdown(props: AssigneeDropdownProps) {
  const {
    options,
    variant = 'default',
    placeholder = 'Select...',
    triggerLabel,
    triggerIcon: TriggerIcon,
    searchable = true,
    searchPlaceholder = 'Search assignees...',
    disabled = false,
    disabledTooltip,
    renderOption = defaultRenderOption,
    emptyText = 'No assignee found.',
    onOpen,
    className,
    triggerTestId,
  } = props;

  const [open, setOpen] = useState(false);

  // Single-select selection used to surface the current assignee at the top of the list.
  const singleSelected = props.mode === 'single' ? props.selected : undefined;
  const singleSelectedTeamId = props.mode === 'single' ? props.selectedTeamId : undefined;

  // Sort options alphabetically (preserving group structure). In single-select mode, also
  // surface the current assignee: move them to the top of their team group, and move that
  // team group to the top of the list.
  const sortedOptions = useMemo<AssigneeOptions>(() => {
    if (!isGroupedOptions(options)) {
      return sortByLabel(options);
    }

    let groups = options.map(group => ({ ...group, options: sortByLabel(group.options) }));

    if (singleSelected) {
      const matchesSelectedTeam = (group: AssigneeOptionGroup) =>
        singleSelectedTeamId
          ? group.id === singleSelectedTeamId
          : group.options.some(o => o.email === singleSelected);

      groups = groups.map(group => {
        if (!matchesSelectedTeam(group)) return group;
        const idx = group.options.findIndex(o => o.email === singleSelected);
        if (idx <= 0) return group;
        const reordered = [...group.options];
        const [sel] = reordered.splice(idx, 1);
        reordered.unshift(sel);
        return { ...group, options: reordered };
      });

      groups = [...groups].sort(
        (a, b) => Number(matchesSelectedTeam(b)) - Number(matchesSelectedTeam(a)),
      );
    }

    return groups;
  }, [options, singleSelected, singleSelectedTeamId]);

  // Flatten options for selection lookups
  const flatOptions = useMemo<AssigneeOption[]>(() => {
    if (isGroupedOptions(sortedOptions)) {
      return sortedOptions.flatMap(group => group.options);
    }
    return sortedOptions;
  }, [sortedOptions]);

  const handleOpenChange = (next: boolean) => {
    if (disabled) return;
    setOpen(next);
    if (next) onOpen?.();
  };

  const handleSelect = (email: string, teamId?: string) => {
    if (props.mode === 'single') {
      props.onChange(email, teamId);
      setOpen(false);
      return;
    }
    const isSelected = props.selected.includes(email);
    const next = isSelected ? props.selected.filter(v => v !== email) : [...props.selected, email];
    props.onChange(next);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.mode === 'multi') props.onChange([]);
    else props.onChange('');
  };

  // For single-mode with a selection, show the avatar + name in the trigger.
  // For multi-mode or no selection, fall back to plain text.
  const selectedOption = useMemo(() => {
    if (props.mode !== 'single' || !props.selected) return undefined;
    return flatOptions.find(o => o.email === props.selected);
  }, [props, flatOptions]);

  const triggerDisplay = useMemo(() => {
    if (triggerLabel) return triggerLabel;

    if (props.mode === 'single') {
      return selectedOption?.name || selectedOption?.email || props.selected || placeholder;
    }

    if (props.selected.length === 0) return placeholder;
    const maxCount = props.maxCount ?? 4;
    if (props.selected.length > maxCount) return `${props.selected.length} selected`;
    return props.selected
      .map(email => flatOptions.find(o => o.email === email)?.name || email)
      .filter(Boolean)
      .join(', ');
  }, [triggerLabel, props, selectedOption, flatOptions, placeholder]);

  // BV-2228, mirroring the read-only cell in AssigneeSelect: the trigger's
  // tooltip repeated the name rendered beside it, while the team was visible only
  // once the menu was open. The option group labels ARE the team names, so this
  // is a lookup rather than a fetch.
  const selectedTeamName = useMemo(() => {
    if (!isGroupedOptions(options)) return undefined;
    return resolveAssigneeTeamName(
      singleSelectedTeamId,
      options.map(group => ({ id: group.id, name: group.label })),
    );
  }, [options, singleSelectedTeamId]);

  const hasSelection =
    props.mode === 'multi'
      ? props.selected.length > 0
      : Boolean(props.selected && props.selected.length > 0);
  const showClearButton = variant === 'default' && hasSelection && !disabled;

  // Show avatar in trigger when single-mode with a resolved option, no override label,
  // and no triggerIcon override (filter/bulk variants use their own icon).
  const showAvatarInTrigger =
    props.mode === 'single' && selectedOption && !triggerLabel && !TriggerIcon;

  const trigger = (
    <Button
      type="button"
      variant={variant === 'default' ? 'outline' : 'ghost'}
      role="combobox"
      aria-expanded={open}
      disabled={disabled}
      data-test-id={triggerTestId}
      className={cn(TRIGGER_VARIANT_CLASSES[variant], className)}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {TriggerIcon && <TriggerIcon className="h-4 w-4 opacity-70 shrink-0" />}
        {showAvatarInTrigger ? (
          <>
            <span className="shrink-0">
              <UserAvatar
                name={selectedOption.name}
                email={selectedOption.email}
                userId={selectedOption.id}
                size="sm"
                available={selectedOption.available ?? false}
              />
            </span>
            <SimpleTooltipV2
              content={formatAssigneeTooltip(
                selectedOption.name || selectedOption.email,
                selectedTeamName,
              )}
              className="min-w-0 flex-1"
            >
              <span className="truncate flex-1 text-left text-sm font-normal">
                {selectedOption.name || selectedOption.email}
              </span>
            </SimpleTooltipV2>
          </>
        ) : (
          <span
            className={cn(
              'truncate flex-1 text-left font-normal',
              !hasSelection && !triggerLabel && 'text-muted-foreground',
            )}
          >
            {triggerDisplay}
          </span>
        )}
      </div>
      <div className="flex items-center shrink-0 ml-2">
        {showClearButton && (
          <>
            <X
              className="h-4 w-4 opacity-50 hover:opacity-100 mr-2"
              onClick={handleClear}
              aria-label="Clear selection"
            />
            <div className="w-px h-4 bg-border mr-2" />
          </>
        )}
        {variant === 'inline' ? (
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        ) : (
          <ChevronDown className="h-4 w-4 opacity-50" />
        )}
      </div>
    </Button>
  );

  const popover = (
    // Wrap in a div with preventDefault — react-router <Link> only navigates if
    // event.defaultPrevented is false, so this stops row navigation when the
    // dropdown sits inside a linked table row.
    // `min-w-0` is scoped to the `inline` variant (the case-list Assignee cell), where the
    // trigger must be free to shrink so its label can truncate. The other variants pin a
    // `min-w-*` on the trigger itself, so letting this wrapper collapse would push the
    // trigger out of it and over the next control in the row.
    <div
      onClick={e => e.preventDefault()}
      className={cn('inline-flex', variant === 'inline' && 'min-w-0 max-w-full')}
    >
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          style={{ minWidth: 'var(--radix-popover-trigger-width)' }}
          onWheel={e => e.stopPropagation()}
        >
          <Command>
            {searchable && <CommandInput placeholder={searchPlaceholder} className="h-9" />}
            <CommandList className="max-h-[300px]">
              <CommandEmpty>{emptyText}</CommandEmpty>
              {isGroupedOptions(sortedOptions) ? (
                sortedOptions.map(group => (
                  <CommandGroup key={group.id} heading={group.label}>
                    {group.options.map(option => (
                      <OptionRow
                        key={option.id}
                        option={option}
                        teamId={group.id}
                        mode={props.mode}
                        isSelected={
                          props.mode === 'multi'
                            ? props.selected.includes(option.email)
                            : props.selected === option.email &&
                              (props.selectedTeamId ? group.id === props.selectedTeamId : true)
                        }
                        onSelect={handleSelect}
                        renderOption={renderOption}
                      />
                    ))}
                  </CommandGroup>
                ))
              ) : (
                <CommandGroup>
                  {sortedOptions.map(option => (
                    <OptionRow
                      key={option.id}
                      option={option}
                      mode={props.mode}
                      isSelected={
                        props.mode === 'multi'
                          ? props.selected.includes(option.email)
                          : props.selected === option.email
                      }
                      onSelect={handleSelect}
                      renderOption={renderOption}
                    />
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );

  if (disabled && disabledTooltip) {
    return (
      <SimpleTooltip content={disabledTooltip} side="top">
        {popover}
      </SimpleTooltip>
    );
  }
  return popover;
}

interface OptionRowProps {
  option: AssigneeOption;
  /** The team id this option is shown under (when rendered inside a group). */
  teamId?: string;
  mode: 'single' | 'multi';
  isSelected: boolean;
  onSelect: (email: string, teamId?: string) => void;
  renderOption: (option: AssigneeOption) => React.ReactNode;
}

function OptionRow({ option, teamId, mode, isSelected, onSelect, renderOption }: OptionRowProps) {
  return (
    <CommandItem
      // Concatenate name + email so Command's built-in filter matches both
      value={`${option.name || ''} ${option.email}`}
      onSelect={() => onSelect(option.email, teamId)}
      className="cursor-pointer"
    >
      {mode === 'multi' ? (
        <Checkbox
          checked={isSelected}
          className="mr-2"
          onCheckedChange={() => onSelect(option.email, teamId)}
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <Check className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
      )}
      <div className="flex-1 min-w-0">{renderOption(option)}</div>
    </CommandItem>
  );
}
